import { GENERATED_WAT_MODULES } from "@generated/wat_modules";
import {
  PROBE_SCHEMA_VERSION,
  get_probe_plan,
  is_wat_probe
} from "@shared/probe_manifest";
import {
  read_probe_page_metadata,
  type ProbePageMetadata
} from "@shared/probe_page_metadata";
import type {
  JsonObject,
  JsonValue,
  ProbeDefinition,
  ProbeObservation,
  ProbeReport,
  ProbeRuntimeContext,
  ProbeTarget,
  RuntimeContextKind
} from "@shared/probe_types";

import { run_with_timeout } from "./promise_timeout";

interface ObservationResult {
  readonly detail: JsonValue;
  readonly supported: boolean | null;
}

type ProbeRunner = () => Promise<ObservationResult>;

interface BatteryManagerLike {
  readonly charging: boolean;
  readonly level: number;
}

interface CodecSupportResult {
  readonly supported: boolean;
}

interface NavigatorWithBattery extends Navigator {
  readonly getBattery?: () => Promise<BatteryManagerLike>;
}

interface NetworkInformationLike {
  readonly downlink?: number;
  readonly effectiveType?: string;
  readonly rtt?: number;
  readonly saveData?: boolean;
}

interface NavigatorWithConnection extends Navigator {
  readonly connection?: NetworkInformationLike;
  readonly mozConnection?: NetworkInformationLike;
}

interface VideoDecoderConfigLike {
  readonly codec: string;
  readonly codedHeight: number;
  readonly codedWidth: number;
}

interface VideoEncoderConfigLike {
  readonly bitrate: number;
  readonly codec: string;
  readonly framerate: number;
  readonly height: number;
  readonly width: number;
}

interface AudioDecoderConfigLike {
  readonly codec: string;
  readonly numberOfChannels: number;
  readonly sampleRate: number;
}

interface AudioEncoderConfigLike {
  readonly bitrate: number;
  readonly codec: string;
  readonly numberOfChannels: number;
  readonly sampleRate: number;
}

interface VideoCodecTestDefinition {
  readonly codec: string;
  readonly decoder_config: VideoDecoderConfigLike;
  readonly encoder_config: VideoEncoderConfigLike;
  readonly id: string;
}

interface AudioCodecTestDefinition {
  readonly codec: string;
  readonly decoder_config: AudioDecoderConfigLike;
  readonly encoder_config: AudioEncoderConfigLike;
  readonly id: string;
}

interface IceGatherStats {
  candidate_count: number;
  readonly candidate_types: Set<string>;
}

interface IceCandidateRelay {
  readonly flush_pending_candidates: () => void;
  readonly handle_candidate: (event: RTCPeerConnectionIceEvent) => void;
}

interface CodecMatrixEntry extends JsonObject {
  readonly codec: string;
  readonly error: string | null;
  readonly id: string;
  readonly supported: boolean;
}

type WebAssemblyInstantiateWithCompileOptions = (
  module_object: BufferSource,
  imports?: WebAssembly.Imports,
  compile_options?: {
    readonly builtins?: readonly string[];
  }
) => Promise<WebAssembly.WebAssemblyInstantiatedSource>;

const EXCEPTIONS_FINAL_MODULE_BASE64 =
  "AGFzbQEAAAABBAFgAAADAgEAChABDgACaR9AAQMAAAsACxoL";
const JS_STRING_BUILTINS_MODULE_BASE64 =
  "AGFzbQEAAAABBgFgAW8BfwIXAQ53YXNtOmpzLXN0cmluZwR0ZXN0AAA=";
const VIDEO_CODEC_TESTS: readonly VideoCodecTestDefinition[] = [
  {
    id: "h264_baseline",
    codec: "avc1.42001E",
    decoder_config: {
      codec: "avc1.42001E",
      codedWidth: 640,
      codedHeight: 360
    },
    encoder_config: {
      codec: "avc1.42001E",
      width: 640,
      height: 360,
      bitrate: 500_000,
      framerate: 30
    }
  },
  {
    id: "vp9_profile_0",
    codec: "vp09.00.10.08",
    decoder_config: {
      codec: "vp09.00.10.08",
      codedWidth: 640,
      codedHeight: 360
    },
    encoder_config: {
      codec: "vp09.00.10.08",
      width: 640,
      height: 360,
      bitrate: 500_000,
      framerate: 30
    }
  },
  {
    id: "av1_main",
    codec: "av01.0.01M.08",
    decoder_config: {
      codec: "av01.0.01M.08",
      codedWidth: 640,
      codedHeight: 360
    },
    encoder_config: {
      codec: "av01.0.01M.08",
      width: 640,
      height: 360,
      bitrate: 500_000,
      framerate: 30
    }
  }
] as const;
const AUDIO_CODEC_TESTS: readonly AudioCodecTestDefinition[] = [
  {
    id: "opus",
    codec: "opus",
    decoder_config: {
      codec: "opus",
      sampleRate: 48_000,
      numberOfChannels: 2
    },
    encoder_config: {
      codec: "opus",
      sampleRate: 48_000,
      numberOfChannels: 2,
      bitrate: 128_000
    }
  }
] as const;

const JS_PROBE_RUNNERS: Readonly<Record<string, ProbeRunner>> = {
  abort_signal_any: async (): Promise<ObservationResult> =>
    boolean_result(typeof AbortSignal.any === "function"),
  abort_signal_timeout: async (): Promise<ObservationResult> =>
    boolean_result(typeof AbortSignal.timeout === "function"),
  array_buffer_transfer: async (): Promise<ObservationResult> =>
    boolean_result(typeof ArrayBuffer.prototype.transfer === "function"),
  atomics_wait_async: async (): Promise<ObservationResult> =>
    boolean_result(typeof Atomics.waitAsync === "function"),
  audio_decoder: async (): Promise<ObservationResult> =>
    boolean_result(typeof AudioDecoder === "function"),
  audio_decoder_codec_support: probe_audio_decoder_codec_support,
  audio_encoder: async (): Promise<ObservationResult> =>
    boolean_result(typeof AudioEncoder === "function"),
  audio_encoder_codec_support: probe_audio_encoder_codec_support,
  battery_status: probe_battery_status,
  big_int_integration: probe_big_int_integration,
  broadcast_channel: async (): Promise<ObservationResult> =>
    boolean_result(probe_broadcast_channel()),
  cache_api: async (): Promise<ObservationResult> =>
    boolean_result(typeof caches !== "undefined"),
  clone_array_buffer: async (): Promise<ObservationResult> =>
    boolean_result(probe_cloneable(new ArrayBuffer(8))),
  clone_readable_stream: async (): Promise<ObservationResult> =>
    boolean_result(probe_cloneable(new ReadableStream())),
  clone_shared_array_buffer: async (): Promise<ObservationResult> =>
    boolean_result(probe_clone_shared_array_buffer()),
  compression_streams: async (): Promise<ObservationResult> =>
    boolean_result(typeof CompressionStream === "function"),
  connection_info: async (): Promise<ObservationResult> => probe_connection_info(),
  conditional_mediation: probe_conditional_mediation,
  credential_management: async (): Promise<ObservationResult> =>
    boolean_result(typeof navigator.credentials !== "undefined"),
  cross_origin_isolated: async (): Promise<ObservationResult> =>
    boolean_result(globalThis.crossOriginIsolated),
  csprng: async (): Promise<ObservationResult> =>
    boolean_result(typeof crypto.getRandomValues === "function"),
  dedicated_worker: async (): Promise<ObservationResult> =>
    boolean_result(typeof Worker === "function"),
  device_memory: async (): Promise<ObservationResult> => ({
    supported: navigator.deviceMemory !== undefined,
    detail: {
      device_memory_gb: navigator.deviceMemory ?? null
    }
  }),
  dynamic_import: async (): Promise<ObservationResult> =>
    boolean_result(probe_dynamic_import()),
  ed25519_generate_key: probe_ed25519,
  encode_into: async (): Promise<ObservationResult> =>
    boolean_result(typeof TextEncoder.prototype.encodeInto === "function"),
  exception_tag_api: async (): Promise<ObservationResult> =>
    boolean_result(typeof WebAssembly.Tag === "function"),
  exceptions_final: async (): Promise<ObservationResult> => probe_exceptions_final(),
  execution_context: async (): Promise<ObservationResult> =>
    execution_context_result(),
  fetch_streaming_request: async (): Promise<ObservationResult> =>
    boolean_result(probe_fetch_streaming_request()),
  finalization_registry: async (): Promise<ObservationResult> =>
    boolean_result(typeof FinalizationRegistry === "function"),
  hardware_concurrency: async (): Promise<ObservationResult> => ({
    supported: navigator.hardwareConcurrency > 0,
    detail: {
      hardware_concurrency: navigator.hardwareConcurrency ?? null
    }
  }),
  high_resolution_time: async (): Promise<ObservationResult> =>
    boolean_result(typeof performance.now === "function"),
  import_maps: async (): Promise<ObservationResult> =>
    boolean_result(HTMLScriptElement.supports?.("importmap") ?? false),
  indexeddb_usable: probe_indexeddb,
  intersection_observer: async (): Promise<ObservationResult> =>
    boolean_result(has_global_function("IntersectionObserver")),
  javascript_runtime: async (): Promise<ObservationResult> =>
    boolean_result(true, { executed: true }),
  js_string_builtins: probe_js_string_builtins,
  jspi_js_api: async (): Promise<ObservationResult> =>
    boolean_result(
      typeof WebAssembly.Suspending === "function" ||
        typeof WebAssembly.promising === "function"
    ),
  max_memory_4gb: async (): Promise<ObservationResult> =>
    boolean_result(probe_max_memory_pages(65_536)),
  memory_pressure_signal: async (): Promise<ObservationResult> =>
    probe_memory_pressure_signal(),
  memory_measurement: async (): Promise<ObservationResult> =>
    boolean_result(
      typeof performance.measureUserAgentSpecificMemory === "function"
    ),
  message_channel: async (): Promise<ObservationResult> =>
    boolean_result(typeof MessageChannel === "function"),
  module_worker: probe_module_worker,
  notification_api: async (): Promise<ObservationResult> =>
    boolean_result(typeof Notification === "function"),
  offscreen_canvas: async (): Promise<ObservationResult> =>
    boolean_result(typeof OffscreenCanvas === "function"),
  offscreen_canvas_2d: async (): Promise<ObservationResult> =>
    boolean_result(probe_offscreen_canvas_2d()),
  offscreen_canvas_webgl: async (): Promise<ObservationResult> =>
    boolean_result(probe_offscreen_canvas_webgl()),
  opfs_available: async (): Promise<ObservationResult> => {
    const storage_manager = navigator.storage;
    return boolean_result(
      storage_manager !== undefined &&
        typeof storage_manager.getDirectory === "function"
    );
  },
  opfs_sync_access: probe_opfs_sync_access,
  page_lifecycle: async (): Promise<ObservationResult> =>
    boolean_result("onfreeze" in document),
  performance_observer: async (): Promise<ObservationResult> =>
    boolean_result(typeof PerformanceObserver === "function"),
  platform_authenticator: probe_platform_authenticator,
  push_manager: async (): Promise<ObservationResult> =>
    boolean_result(typeof PushManager !== "undefined"),
  readable_stream_async_iterator: async (): Promise<ObservationResult> =>
    boolean_result(probe_readable_stream_async_iterator()),
  request_idle_callback: async (): Promise<ObservationResult> =>
    boolean_result(typeof requestIdleCallback === "function"),
  resizable_array_buffer: async (): Promise<ObservationResult> =>
    boolean_result(probe_resizable_array_buffer()),
  reporting_observer: async (): Promise<ObservationResult> =>
    boolean_result(has_global_function("ReportingObserver")),
  resize_observer: async (): Promise<ObservationResult> =>
    boolean_result(has_global_function("ResizeObserver")),
  rtc_data_channel: async (): Promise<ObservationResult> =>
    boolean_result(probe_rtc_data_channel()),
  rtc_data_channel_loopback: probe_rtc_data_channel_loopback,
  scheduler_post_task: async (): Promise<ObservationResult> =>
    boolean_result(typeof scheduler?.postTask === "function"),
  service_worker_api: async (): Promise<ObservationResult> =>
    boolean_result("serviceWorker" in navigator),
  shared_array_buffer_constructible: async (): Promise<ObservationResult> =>
    boolean_result(probe_shared_array_buffer()),
  shared_worker_ctor: async (): Promise<ObservationResult> =>
    boolean_result(typeof SharedWorker === "function"),
  storage_estimate: probe_storage_estimate,
  storage_event: async (): Promise<ObservationResult> =>
    boolean_result(typeof StorageEvent === "function"),
  storage_persist: async (): Promise<ObservationResult> => {
    const storage_manager = navigator.storage;
    return boolean_result(
      storage_manager !== undefined &&
        typeof storage_manager.persist === "function"
    );
  },
  storage_quota: probe_storage_quota,
  streaming_compilation: async (): Promise<ObservationResult> =>
    boolean_result(typeof WebAssembly.compileStreaming === "function"),
  structured_clone: async (): Promise<ObservationResult> =>
    boolean_result(typeof structuredClone === "function"),
  subtle_crypto: async (): Promise<ObservationResult> =>
    boolean_result(typeof crypto.subtle !== "undefined"),
  text_encoder: async (): Promise<ObservationResult> =>
    boolean_result(typeof TextEncoder === "function"),
  transferable_array_buffer: async (): Promise<ObservationResult> =>
    boolean_result(probe_transferable((): ArrayBuffer => new ArrayBuffer(8))),
  transferable_message_port: async (): Promise<ObservationResult> =>
    boolean_result(
      probe_transferable((): MessagePort => new MessageChannel().port1)
    ),
  transferable_offscreen_canvas: async (): Promise<ObservationResult> =>
    boolean_result(probe_transferable_offscreen_canvas()),
  transferable_readable_stream: async (): Promise<ObservationResult> =>
    boolean_result(probe_transferable_readable_stream()),
  transferable_streams: async (): Promise<ObservationResult> =>
    boolean_result(probe_transferable_readable_stream()),
  type_reflection: async (): Promise<ObservationResult> => probe_type_reflection(),
  video_decoder: async (): Promise<ObservationResult> =>
    boolean_result(typeof VideoDecoder === "function"),
  video_decoder_codec_support: probe_video_decoder_codec_support,
  video_encoder: async (): Promise<ObservationResult> =>
    boolean_result(typeof VideoEncoder === "function"),
  video_encoder_codec_support: probe_video_encoder_codec_support,
  wasm_in_worker: probe_wasm_in_worker,
  weak_ref: async (): Promise<ObservationResult> =>
    boolean_result(typeof WeakRef === "function"),
  webauthn: async (): Promise<ObservationResult> =>
    boolean_result(typeof PublicKeyCredential === "function"),
  webgl: async (): Promise<ObservationResult> => probe_webgl(),
  webgpu: probe_webgpu,
  webrtc_peer_connection: async (): Promise<ObservationResult> =>
    boolean_result(typeof RTCPeerConnection === "function"),
  webrtc_trickle_ice: probe_webrtc_trickle_ice,
  webtransport_ctor: async (): Promise<ObservationResult> =>
    boolean_result(typeof WebTransport === "function"),
  websocket_ctor: async (): Promise<ObservationResult> =>
    boolean_result(typeof WebSocket === "function"),
  web_locks_usable: probe_web_locks,
  x25519_generate_key: probe_x25519
};

/**
 * Executes the ordered probe plan and builds a report envelope.
 */
export async function execute_probe_session(
  current_url: URL
): Promise<ProbeReport> {
  const page_metadata = read_probe_page_metadata(current_url);
  const observations = await run_probe_plan();
  const runtime = capture_runtime_context(current_url);
  const target = read_probe_target(current_url, page_metadata);

  return {
    schema_version: PROBE_SCHEMA_VERSION,
    report_id: crypto.randomUUID(),
    probe_id: read_probe_id(page_metadata),
    status: determine_report_status(observations),
    submitted_at: new Date().toISOString(),
    target,
    runtime,
    observations
  };
}

/**
 * Sends a probe report to the Worker ingestion endpoint.
 */
export async function submit_probe_report(report: ProbeReport): Promise<void> {
  const response = await fetch("/api/result", {
    method: "POST",
    headers: {
      "content-type": "application/json"
    },
    body: JSON.stringify(report)
  });

  if (!response.ok) {
    throw new Error(`probe report upload failed with status ${response.status}`);
  }
}

async function run_probe_plan(): Promise<readonly ProbeObservation[]> {
  const observations: ProbeObservation[] = [];

  for (const probe of get_probe_plan()) {
    observations.push(await run_probe(probe));
  }

  return observations;
}

async function run_probe(probe: ProbeDefinition): Promise<ProbeObservation> {
  const start_time = performance.now();

  try {
    const probe_result = is_wat_probe(probe)
      ? run_wat_probe(probe.id)
      : run_js_probe(probe.id);
    const result = await run_with_timeout(
      probe_result,
      probe.timeout_ms,
      `probe ${probe.id} timed out after ${probe.timeout_ms}ms`
    );

    return {
      id: probe.id,
      supported: result.supported,
      duration_ms: Math.round((performance.now() - start_time) * 100) / 100,
      detail: result.detail,
      error_message: null
    };
  } catch (error) {
    return {
      id: probe.id,
      supported: null,
      duration_ms: Math.round((performance.now() - start_time) * 100) / 100,
      detail: {
        error: error_to_message(error)
      },
      error_message: error_to_message(error)
    };
  }
}

async function run_js_probe(probe_id: string): Promise<ObservationResult> {
  const runner = JS_PROBE_RUNNERS[probe_id];
  if (runner === undefined) {
    throw new Error(`missing JavaScript runner for probe ${probe_id}`);
  }

  return runner();
}

async function run_wat_probe(probe_id: string): Promise<ObservationResult> {
  const bytes = GENERATED_WAT_MODULES[probe_id];
  if (bytes === undefined) {
    throw new Error(`missing generated wasm bytes for probe ${probe_id}`);
  }

  return boolean_result(WebAssembly.validate(new Uint8Array(bytes)), {
    byte_length: bytes.length
  });
}

function read_probe_id(page_metadata: ProbePageMetadata | null): string {
  return page_metadata?.probe_id ?? crypto.randomUUID();
}

function read_probe_target(
  current_url: URL,
  page_metadata: ProbePageMetadata | null
): ProbeTarget {
  return {
    platform_key: page_metadata?.platform_key ?? null,
    browser_key: page_metadata?.browser_key ?? null,
    browse_url: null,
    target_url: current_url.href,
    source: page_metadata?.launcher ?? "manual"
  };
}

function capture_runtime_context(current_url: URL): ProbeRuntimeContext {
  return {
    href: current_url.href,
    context_kind: infer_context_kind(),
    user_agent: navigator.userAgent,
    platform: navigator.platform ?? null,
    language: navigator.language ?? null,
    languages: [...navigator.languages],
    is_secure_context: globalThis.isSecureContext,
    cross_origin_isolated: globalThis.crossOriginIsolated,
    hardware_concurrency: navigator.hardwareConcurrency ?? null,
    device_memory_gb: navigator.deviceMemory ?? null
  };
}

function infer_context_kind(): RuntimeContextKind {
  if (typeof window !== "undefined" && typeof document !== "undefined") {
    return "window";
  }

  if (typeof DedicatedWorkerGlobalScope !== "undefined") {
    return "dedicated_worker";
  }

  if (typeof SharedWorkerGlobalScope !== "undefined") {
    return "shared_worker";
  }

  if (typeof ServiceWorkerGlobalScope !== "undefined") {
    return "service_worker";
  }

  return "unknown";
}

function determine_report_status(
  observations: readonly ProbeObservation[]
): ProbeReport["status"] {
  const error_count = observations.filter(
    (observation): boolean => observation.error_message !== null
  ).length;

  if (error_count === 0) {
    return "ok";
  }

  return error_count === observations.length ? "error" : "partial";
}

function boolean_result(
  supported: boolean,
  extra_detail: JsonObject = {}
): ObservationResult {
  return {
    supported,
    detail: {
      supported,
      ...extra_detail
    }
  };
}

function error_to_message(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return String(error);
}

function execution_context_result(): ObservationResult {
  return {
    supported: true,
    detail: {
      is_window:
        typeof window !== "undefined" && typeof document !== "undefined",
      is_dedicated_worker:
        typeof DedicatedWorkerGlobalScope !== "undefined",
      is_shared_worker:
        typeof SharedWorkerGlobalScope !== "undefined",
      is_service_worker:
        typeof ServiceWorkerGlobalScope !== "undefined",
      is_workerd:
        navigator.userAgent.includes("Cloudflare-Workers")
    }
  };
}

function has_global_function(global_name: string): boolean {
  return typeof Reflect.get(globalThis as object, global_name) === "function";
}

function decode_base64_module(module_base64: string): Uint8Array<ArrayBuffer> {
  const decoded_bytes = Array.from(
    atob(module_base64),
    (character: string): number => {
      return character.codePointAt(0) ?? 0;
    }
  );
  const module_bytes = new Uint8Array(new ArrayBuffer(decoded_bytes.length));
  module_bytes.set(decoded_bytes);
  return module_bytes;
}

function build_codec_matrix_result(
  api_available: boolean,
  codec_support: readonly CodecMatrixEntry[]
): ObservationResult {
  const has_supported_codec = codec_support.some(
    (codec_entry): boolean => codec_entry.supported
  );
  const supported = api_available && has_supported_codec;

  return {
    supported,
    detail: {
      supported,
      api_available,
      codec_support
    }
  };
}

function create_ice_gather_stats(): IceGatherStats {
  return {
    candidate_count: 0,
    candidate_types: new Set<string>()
  };
}

function track_ice_candidate(
  ice_gather_stats: IceGatherStats,
  candidate: RTCIceCandidate
): void {
  ice_gather_stats.candidate_count += 1;

  const candidate_type = parse_ice_candidate_type(candidate.candidate);
  if (candidate_type !== null) {
    ice_gather_stats.candidate_types.add(candidate_type);
  }
}

function parse_ice_candidate_type(candidate_line: string): string | null {
  const candidate_match = / typ ([a-z]+)/.exec(candidate_line);
  return candidate_match?.[1] ?? null;
}

function serialize_ice_candidate_types(
  ice_gather_stats: IceGatherStats
): readonly string[] {
  return [...ice_gather_stats.candidate_types].sort();
}

function snapshot_ice_candidate(
  candidate: RTCIceCandidate
): RTCIceCandidateInit {
  return {
    candidate: candidate.candidate,
    sdpMid: candidate.sdpMid,
    sdpMLineIndex: candidate.sdpMLineIndex
  };
}

function create_ice_candidate_relay(
  target: RTCPeerConnection,
  ice_gather_stats: IceGatherStats,
  candidate_exchange_errors: string[]
): IceCandidateRelay {
  const pending_candidates: RTCIceCandidateInit[] = [];

  const flush_pending_candidates = (): void => {
    if (target.remoteDescription === null) {
      return;
    }

    while (pending_candidates.length > 0) {
      const candidate = pending_candidates.shift();
      if (candidate === undefined) {
        return;
      }

      void target.addIceCandidate(candidate).catch((error: unknown): void => {
        candidate_exchange_errors.push(error_to_message(error));
      });
    }
  };

  return {
    flush_pending_candidates,
    handle_candidate: (event: RTCPeerConnectionIceEvent): void => {
      if (event.candidate === null) {
        return;
      }

      track_ice_candidate(ice_gather_stats, event.candidate);
      pending_candidates.push(snapshot_ice_candidate(event.candidate));
      flush_pending_candidates();
    }
  };
}

function close_peer_connection_quietly(peer_connection: RTCPeerConnection): void {
  try {
    peer_connection.close();
  } catch {
    // Ignore close failures during probe cleanup.
  }
}

function probe_exceptions_final(): ObservationResult {
  try {
    new WebAssembly.Module(decode_base64_module(EXCEPTIONS_FINAL_MODULE_BASE64));
    return boolean_result(true);
  } catch (error) {
    return boolean_result(false, {
      error: error_to_message(error)
    });
  }
}

async function probe_js_string_builtins(): Promise<ObservationResult> {
  const instantiate_with_compile_options =
    WebAssembly.instantiate as unknown as WebAssemblyInstantiateWithCompileOptions;

  try {
    await instantiate_with_compile_options(
      decode_base64_module(JS_STRING_BUILTINS_MODULE_BASE64),
      {},
      { builtins: ["js-string"] }
    );
    return boolean_result(true);
  } catch (error) {
    return boolean_result(false, {
      error: error_to_message(error)
    });
  }
}

function probe_type_reflection(): ObservationResult {
  const has_webassembly_function = "Function" in WebAssembly;

  return boolean_result(has_webassembly_function, {
    has_webassembly_function
  });
}

function probe_connection_info(): ObservationResult {
  const extended_navigator = navigator as NavigatorWithConnection;
  const connection =
    extended_navigator.connection ?? extended_navigator.mozConnection ?? null;

  return {
    supported: connection !== null,
    detail: {
      supported: connection !== null,
      effective_type: connection?.effectiveType ?? null,
      downlink_mbps: connection?.downlink ?? null,
      rtt_ms: connection?.rtt ?? null,
      save_data: connection?.saveData ?? null
    }
  };
}

async function probe_battery_status(): Promise<ObservationResult> {
  const extended_navigator = navigator as NavigatorWithBattery;
  if (typeof extended_navigator.getBattery !== "function") {
    return boolean_result(false);
  }

  try {
    const battery = await run_with_timeout(
      extended_navigator.getBattery.call(extended_navigator),
      500,
      "battery status timed out"
    );

    return {
      supported: true,
      detail: {
        supported: true,
        charging: battery.charging,
        level: battery.level
      }
    };
  } catch (error) {
    return boolean_result(false, {
      error: error_to_message(error)
    });
  }
}

function probe_memory_pressure_signal(): ObservationResult {
  const has_memory_pressure_event = "onmemorypressure" in globalThis;
  const has_memory_measurement =
    typeof performance.measureUserAgentSpecificMemory === "function";
  const supported = has_memory_pressure_event || has_memory_measurement;

  return {
    supported,
    detail: {
      supported,
      has_memory_pressure_event,
      has_memory_measurement
    }
  };
}

function probe_readable_stream_async_iterator(): boolean {
  try {
    return (
      typeof ReadableStream === "function" &&
      Symbol.asyncIterator in ReadableStream.prototype
    );
  } catch {
    return false;
  }
}

async function probe_video_decoder_codec_support(): Promise<ObservationResult> {
  if (
    typeof VideoDecoder !== "function" ||
    typeof VideoDecoder.isConfigSupported !== "function"
  ) {
    return build_codec_matrix_result(false, []);
  }

  const is_config_supported = VideoDecoder.isConfigSupported as (
    config: VideoDecoderConfigLike
  ) => Promise<CodecSupportResult>;
  const codec_support = await Promise.all(
    VIDEO_CODEC_TESTS.map(
      async (codec_test): Promise<CodecMatrixEntry> => {
        try {
          const support = await is_config_supported(codec_test.decoder_config);

          return {
            id: codec_test.id,
            codec: codec_test.codec,
            supported: support.supported,
            error: null
          };
        } catch (error) {
          return {
            id: codec_test.id,
            codec: codec_test.codec,
            supported: false,
            error: error_to_message(error)
          };
        }
      }
    )
  );

  return build_codec_matrix_result(true, codec_support);
}

async function probe_video_encoder_codec_support(): Promise<ObservationResult> {
  if (
    typeof VideoEncoder !== "function" ||
    typeof VideoEncoder.isConfigSupported !== "function"
  ) {
    return build_codec_matrix_result(false, []);
  }

  const is_config_supported = VideoEncoder.isConfigSupported as (
    config: VideoEncoderConfigLike
  ) => Promise<CodecSupportResult>;
  const codec_support = await Promise.all(
    VIDEO_CODEC_TESTS.map(
      async (codec_test): Promise<CodecMatrixEntry> => {
        try {
          const support = await is_config_supported(codec_test.encoder_config);

          return {
            id: codec_test.id,
            codec: codec_test.codec,
            supported: support.supported,
            error: null
          };
        } catch (error) {
          return {
            id: codec_test.id,
            codec: codec_test.codec,
            supported: false,
            error: error_to_message(error)
          };
        }
      }
    )
  );

  return build_codec_matrix_result(true, codec_support);
}

async function probe_audio_decoder_codec_support(): Promise<ObservationResult> {
  if (
    typeof AudioDecoder !== "function" ||
    typeof AudioDecoder.isConfigSupported !== "function"
  ) {
    return build_codec_matrix_result(false, []);
  }

  const is_config_supported = AudioDecoder.isConfigSupported as (
    config: AudioDecoderConfigLike
  ) => Promise<CodecSupportResult>;
  const codec_support = await Promise.all(
    AUDIO_CODEC_TESTS.map(
      async (codec_test): Promise<CodecMatrixEntry> => {
        try {
          const support = await is_config_supported(codec_test.decoder_config);

          return {
            id: codec_test.id,
            codec: codec_test.codec,
            supported: support.supported,
            error: null
          };
        } catch (error) {
          return {
            id: codec_test.id,
            codec: codec_test.codec,
            supported: false,
            error: error_to_message(error)
          };
        }
      }
    )
  );

  return build_codec_matrix_result(true, codec_support);
}

async function probe_audio_encoder_codec_support(): Promise<ObservationResult> {
  if (
    typeof AudioEncoder !== "function" ||
    typeof AudioEncoder.isConfigSupported !== "function"
  ) {
    return build_codec_matrix_result(false, []);
  }

  const is_config_supported = AudioEncoder.isConfigSupported as (
    config: AudioEncoderConfigLike
  ) => Promise<CodecSupportResult>;
  const codec_support = await Promise.all(
    AUDIO_CODEC_TESTS.map(
      async (codec_test): Promise<CodecMatrixEntry> => {
        try {
          const support = await is_config_supported(codec_test.encoder_config);

          return {
            id: codec_test.id,
            codec: codec_test.codec,
            supported: support.supported,
            error: null
          };
        } catch (error) {
          return {
            id: codec_test.id,
            codec: codec_test.codec,
            supported: false,
            error: error_to_message(error)
          };
        }
      }
    )
  );

  return build_codec_matrix_result(true, codec_support);
}

async function probe_webrtc_trickle_ice(): Promise<ObservationResult> {
  if (typeof RTCPeerConnection !== "function") {
    return boolean_result(false);
  }

  const peer_connection = new RTCPeerConnection({ iceServers: [] });
  const ice_gather_stats = create_ice_gather_stats();
  let saw_end_of_candidates = false;

  try {
    const gather_promise = new Promise<void>((resolve): void => {
      let settled = false;
      const finish = (): void => {
        if (settled) {
          return;
        }

        settled = true;
        resolve();
      };

      peer_connection.onicecandidate = (
        event: RTCPeerConnectionIceEvent
      ): void => {
        if (event.candidate === null) {
          saw_end_of_candidates = true;
          finish();
          return;
        }

        track_ice_candidate(ice_gather_stats, event.candidate);
      };

      peer_connection.onicegatheringstatechange = (): void => {
        if (peer_connection.iceGatheringState === "complete") {
          finish();
        }
      };
    });

    peer_connection.createDataChannel("__browser_probe__");
    const offer = await peer_connection.createOffer();
    await peer_connection.setLocalDescription(offer);
    await run_with_timeout(
      gather_promise,
      1_500,
      "WebRTC ICE gathering timed out"
    );

    const supported =
      saw_end_of_candidates && ice_gather_stats.candidate_count > 0;

    return {
      supported,
      detail: {
        supported,
        candidate_count: ice_gather_stats.candidate_count,
        candidate_types: serialize_ice_candidate_types(ice_gather_stats),
        end_of_candidates: saw_end_of_candidates,
        ice_gathering_state: peer_connection.iceGatheringState,
        can_trickle_ice_candidates: peer_connection.canTrickleIceCandidates
      }
    };
  } catch (error) {
    return boolean_result(false, {
      error: error_to_message(error),
      candidate_count: ice_gather_stats.candidate_count,
      candidate_types: serialize_ice_candidate_types(ice_gather_stats)
    });
  } finally {
    close_peer_connection_quietly(peer_connection);
  }
}

async function probe_rtc_data_channel_loopback(): Promise<ObservationResult> {
  if (typeof RTCPeerConnection !== "function") {
    return boolean_result(false);
  }

  const local_connection = new RTCPeerConnection({ iceServers: [] });
  const remote_connection = new RTCPeerConnection({ iceServers: [] });
  const local_ice_gather_stats = create_ice_gather_stats();
  const remote_ice_gather_stats = create_ice_gather_stats();
  const candidate_exchange_errors: string[] = [];
  const local_candidate_relay = create_ice_candidate_relay(
    remote_connection,
    local_ice_gather_stats,
    candidate_exchange_errors
  );
  const remote_candidate_relay = create_ice_candidate_relay(
    local_connection,
    remote_ice_gather_stats,
    candidate_exchange_errors
  );
  const local_channel = local_connection.createDataChannel("__browser_probe__");
  let local_channel_state: string = local_channel.readyState;
  let remote_channel_state = "not_received";

  try {
    const ping_pong_promise = new Promise<boolean>((resolve): void => {
      let settled = false;
      const finish = (value: boolean): void => {
        if (settled) {
          return;
        }

        settled = true;
        resolve(value);
      };

      local_channel.onopen = (): void => {
        local_channel_state = local_channel.readyState;

        try {
          local_channel.send("ping");
        } catch {
          finish(false);
        }
      };
      local_channel.onclose = (): void => {
        local_channel_state = local_channel.readyState;
        finish(false);
      };
      local_channel.onerror = (): void => finish(false);
      local_channel.onmessage = (event: MessageEvent): void => {
        if (event.data === "pong") {
          finish(true);
        }
      };

      remote_connection.ondatachannel = (event: RTCDataChannelEvent): void => {
        const remote_channel = event.channel;
        remote_channel_state = remote_channel.readyState;
        remote_channel.onopen = (): void => {
          remote_channel_state = remote_channel.readyState;
        };
        remote_channel.onclose = (): void => {
          remote_channel_state = remote_channel.readyState;
        };
        remote_channel.onerror = (): void => finish(false);
        remote_channel.onmessage = (message_event: MessageEvent): void => {
          if (message_event.data === "ping") {
            remote_channel.send("pong");
          }
        };
      };
    });

    local_connection.onicecandidate = (
      event: RTCPeerConnectionIceEvent
    ): void => {
      local_candidate_relay.handle_candidate(event);
    };
    remote_connection.onicecandidate = (
      event: RTCPeerConnectionIceEvent
    ): void => {
      remote_candidate_relay.handle_candidate(event);
    };

    const offer = await local_connection.createOffer();
    await local_connection.setLocalDescription(offer);
    await remote_connection.setRemoteDescription(offer);
    local_candidate_relay.flush_pending_candidates();

    const answer = await remote_connection.createAnswer();
    await remote_connection.setLocalDescription(answer);
    await local_connection.setRemoteDescription(answer);
    local_candidate_relay.flush_pending_candidates();
    remote_candidate_relay.flush_pending_candidates();

    const supported = await run_with_timeout(
      ping_pong_promise,
      1_500,
      "RTC data channel loopback timed out"
    );

    return {
      supported,
      detail: {
        supported,
        local_candidate_count: local_ice_gather_stats.candidate_count,
        local_candidate_types: serialize_ice_candidate_types(
          local_ice_gather_stats
        ),
        remote_candidate_count: remote_ice_gather_stats.candidate_count,
        remote_candidate_types: serialize_ice_candidate_types(
          remote_ice_gather_stats
        ),
        local_channel_state,
        remote_channel_state,
        candidate_exchange_error_count: candidate_exchange_errors.length
      }
    };
  } catch (error) {
    return boolean_result(false, {
      error: error_to_message(error),
      local_candidate_count: local_ice_gather_stats.candidate_count,
      local_candidate_types: serialize_ice_candidate_types(
        local_ice_gather_stats
      ),
      remote_candidate_count: remote_ice_gather_stats.candidate_count,
      remote_candidate_types: serialize_ice_candidate_types(
        remote_ice_gather_stats
      ),
      candidate_exchange_errors
    });
  } finally {
    close_peer_connection_quietly(local_connection);
    close_peer_connection_quietly(remote_connection);
  }
}

async function probe_big_int_integration(): Promise<ObservationResult> {
  if (typeof BigInt !== "function") {
    return boolean_result(false);
  }

  const bytes = GENERATED_WAT_MODULES["big_int_roundtrip_helper"];
  if (bytes === undefined) {
    return {
      supported: null,
      detail: {
        reason: "missing helper module"
      }
    };
  }

  const module = new WebAssembly.Module(new Uint8Array(bytes));
  const instance = new WebAssembly.Instance(module);
  const export_value = instance.exports["id"];
  if (typeof export_value !== "function") {
    return boolean_result(false);
  }

  const id_function = export_value as (value: bigint) => bigint;
  const result = id_function(42n);
  return boolean_result(result === 42n, {
    value_type: typeof result
  });
}

async function probe_indexeddb(): Promise<ObservationResult> {
  const supported = await new Promise<boolean>((resolve): void => {
    try {
      const request = indexedDB.open("__browser_probe__", 1);
      request.onsuccess = (): void => {
        request.result.close();
        void indexedDB.deleteDatabase("__browser_probe__");
        resolve(true);
      };
      request.onerror = (): void => resolve(false);
    } catch {
      resolve(false);
    }
  });

  return boolean_result(supported);
}

async function probe_opfs_sync_access(): Promise<ObservationResult> {
  if (typeof FileSystemFileHandle === "undefined") {
    return boolean_result(false);
  }

  return boolean_result(
    typeof FileSystemFileHandle.prototype.createSyncAccessHandle === "function"
  );
}

async function probe_storage_estimate(): Promise<ObservationResult> {
  const storage_manager = navigator.storage;
  return boolean_result(
    storage_manager !== undefined &&
      typeof storage_manager.estimate === "function"
  );
}

async function probe_storage_quota(): Promise<ObservationResult> {
  const storage_manager = navigator.storage;
  if (storage_manager === undefined || typeof storage_manager.estimate !== "function") {
    return boolean_result(false);
  }

  try {
    const estimate = await storage_manager.estimate();
    const quota = estimate.quota ?? null;
    const usage = estimate.usage ?? null;

    return {
      supported: quota !== null,
      detail: {
        quota,
        usage,
        usage_ratio:
          quota !== null && usage !== null && quota > 0 ? usage / quota : null
      }
    };
  } catch (error) {
    return boolean_result(false, {
      error: error_to_message(error)
    });
  }
}

function probe_fetch_streaming_request(): boolean {
  try {
    new Request("https://example.invalid", {
      method: "POST",
      body: new ReadableStream(),
      duplex: "half"
    });
    return true;
  } catch {
    return false;
  }
}

async function probe_module_worker(): Promise<ObservationResult> {
  if (typeof Worker !== "function") {
    return boolean_result(false);
  }

  const blob = new Blob(['postMessage("ok"); export {};'], {
    type: "application/javascript"
  });
  const worker_url = URL.createObjectURL(blob);

  try {
    const supported = await new Promise<boolean>((resolve): void => {
      let settled = false;

      const worker = new Worker(worker_url, { type: "module" });
      const finish = (value: boolean): void => {
        if (settled) {
          return;
        }

        settled = true;
        worker.terminate();
        URL.revokeObjectURL(worker_url);
        resolve(value);
      };

      worker.onmessage = (): void => finish(true);
      worker.onerror = (): void => finish(false);
      window.setTimeout((): void => finish(false), 250);
    });

    return boolean_result(supported);
  } catch {
    URL.revokeObjectURL(worker_url);
    return boolean_result(false);
  }
}

async function probe_wasm_in_worker(): Promise<ObservationResult> {
  if (typeof Worker !== "function") {
    return boolean_result(false);
  }

  const source = `
    try {
      new WebAssembly.Module(new Uint8Array([0,97,115,109,1,0,0,0]));
      postMessage(true);
    } catch {
      postMessage(false);
    }
  `;
  const blob = new Blob([source], { type: "application/javascript" });
  const worker_url = URL.createObjectURL(blob);

  try {
    const supported = await new Promise<boolean>((resolve): void => {
      let settled = false;

      const worker = new Worker(worker_url);
      const finish = (value: boolean): void => {
        if (settled) {
          return;
        }

        settled = true;
        worker.terminate();
        URL.revokeObjectURL(worker_url);
        resolve(value);
      };

      worker.onmessage = (event: MessageEvent<boolean>): void => finish(event.data);
      worker.onerror = (): void => finish(false);
      window.setTimeout((): void => finish(false), 250);
    });

    return boolean_result(supported);
  } catch {
    URL.revokeObjectURL(worker_url);
    return boolean_result(false);
  }
}

async function probe_web_locks(): Promise<ObservationResult> {
  if (navigator.locks === undefined) {
    return boolean_result(false);
  }

  try {
    const supported = await new Promise<boolean>((resolve): void => {
      let settled = false;

      const finish = (value: boolean): void => {
        if (settled) {
          return;
        }

        settled = true;
        resolve(value);
      };

      void navigator.locks.request(
        "__browser_probe__",
        { ifAvailable: true },
        (lock): boolean => {
          finish(lock !== null);
          return true;
        }
      );

      window.setTimeout((): void => finish(false), 250);
    });

    return boolean_result(supported);
  } catch (error) {
    return boolean_result(false, {
      error: error_to_message(error)
    });
  }
}

async function probe_ed25519(): Promise<ObservationResult> {
  try {
    await crypto.subtle.generateKey({ name: "Ed25519" }, false, [
      "sign",
      "verify"
    ]);
    return boolean_result(true);
  } catch (error) {
    return boolean_result(false, {
      error: error_to_message(error)
    });
  }
}

async function probe_x25519(): Promise<ObservationResult> {
  try {
    await crypto.subtle.generateKey({ name: "X25519" }, false, ["deriveBits"]);
    return boolean_result(true);
  } catch (error) {
    return boolean_result(false, {
      error: error_to_message(error)
    });
  }
}

async function probe_platform_authenticator(): Promise<ObservationResult> {
  if (typeof PublicKeyCredential === "undefined") {
    return boolean_result(false);
  }

  const method =
    PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable;
  if (typeof method !== "function") {
    return boolean_result(false);
  }

  try {
    const supported = await run_with_timeout(
      method.call(PublicKeyCredential),
      250,
      "platform authenticator availability timed out"
    );
    return boolean_result(supported);
  } catch (error) {
    return boolean_result(false, {
      error: error_to_message(error)
    });
  }
}

async function probe_conditional_mediation(): Promise<ObservationResult> {
  if (typeof PublicKeyCredential === "undefined") {
    return boolean_result(false);
  }

  const method = PublicKeyCredential.isConditionalMediationAvailable;
  return boolean_result(typeof method === "function");
}

function probe_cloneable(value: JsonValue | ArrayBuffer | ReadableStream): boolean {
  try {
    structuredClone(value);
    return true;
  } catch {
    return false;
  }
}

function probe_clone_shared_array_buffer(): boolean {
  try {
    const buffer = new SharedArrayBuffer(8);
    structuredClone(buffer);
    return true;
  } catch {
    return false;
  }
}

function probe_transferable<TValue extends Transferable>(
  create_value: () => TValue
): boolean {
  try {
    const channel = new MessageChannel();
    const value = create_value();
    channel.port1.postMessage(value, [value]);
    channel.port1.close();
    channel.port2.close();
    return true;
  } catch {
    return false;
  }
}

function probe_transferable_offscreen_canvas(): boolean {
  if (typeof OffscreenCanvas !== "function") {
    return false;
  }

  return probe_transferable(
    (): OffscreenCanvas => new OffscreenCanvas(1, 1)
  );
}

function probe_transferable_readable_stream(): boolean {
  return probe_transferable(
    (): ReadableStream => new ReadableStream()
  );
}

function probe_shared_array_buffer(): boolean {
  try {
    new SharedArrayBuffer(1);
    return true;
  } catch {
    return false;
  }
}

function probe_max_memory_pages(pages: number): boolean {
  try {
    new WebAssembly.Memory({ initial: 0, maximum: pages });
    return true;
  } catch {
    return false;
  }
}

function probe_rtc_data_channel(): boolean {
  if (typeof RTCPeerConnection !== "function") {
    return false;
  }

  try {
    const peer_connection = new RTCPeerConnection();
    const data_channel = peer_connection.createDataChannel("__browser_probe__");
    data_channel.close();
    peer_connection.close();
    return true;
  } catch {
    return false;
  }
}

function probe_broadcast_channel(): boolean {
  if (typeof BroadcastChannel !== "function") {
    return false;
  }

  try {
    const channel = new BroadcastChannel("__browser_probe__");
    channel.postMessage("ping");
    channel.close();
    return true;
  } catch {
    return false;
  }
}

function probe_dynamic_import(): boolean {
  try {
    new Function('return import("data:text/javascript,export default 1")');
    return true;
  } catch {
    return false;
  }
}

function probe_resizable_array_buffer(): boolean {
  try {
    const buffer = new ArrayBuffer(8, { maxByteLength: 16 });
    if (typeof buffer.resize !== "function") {
      return false;
    }

    buffer.resize(12);
    return buffer.byteLength === 12;
  } catch {
    return false;
  }
}

function probe_offscreen_canvas_2d(): boolean {
  try {
    const canvas = new OffscreenCanvas(1, 1);
    return canvas.getContext("2d") !== null;
  } catch {
    return false;
  }
}

function probe_offscreen_canvas_webgl(): boolean {
  try {
    const canvas = new OffscreenCanvas(1, 1);
    return (
      canvas.getContext("webgl2") !== null ||
      canvas.getContext("webgl") !== null
    );
  } catch {
    return false;
  }
}

async function probe_webgpu(): Promise<ObservationResult> {
  const gpu = navigator.gpu;
  if (gpu === undefined) {
    return boolean_result(false);
  }

  try {
    const adapter = await gpu.requestAdapter();
    if (adapter === null) {
      return boolean_result(false);
    }

    const adapter_info = adapter.requestAdapterInfo
      ? await adapter.requestAdapterInfo()
      : null;

    return {
      supported: true,
      detail: {
        supported: true,
        architecture: adapter_info?.architecture ?? null,
        vendor: adapter_info?.vendor ?? null,
        feature_count: [...adapter.features].length,
        max_buffer_size: adapter.limits.maxBufferSize,
        max_compute_workgroup_size_x:
          adapter.limits.maxComputeWorkgroupSizeX,
        max_compute_invocations_per_workgroup:
          adapter.limits.maxComputeInvocationsPerWorkgroup
      }
    };
  } catch (error) {
    return boolean_result(false, {
      error: error_to_message(error)
    });
  }
}

function probe_webgl(): ObservationResult {
  const canvas = document.createElement("canvas");
  const webgl2 = canvas.getContext("webgl2");
  if (webgl2 !== null) {
    return {
      supported: true,
      detail: {
        supported: true,
        version: 2,
        max_texture_size: webgl2.getParameter(webgl2.MAX_TEXTURE_SIZE) as number
      }
    };
  }

  const webgl1 = canvas.getContext("webgl");
  if (webgl1 !== null) {
    return {
      supported: true,
      detail: {
        supported: true,
        version: 1,
        max_texture_size: webgl1.getParameter(webgl1.MAX_TEXTURE_SIZE) as number
      }
    };
  }

  return boolean_result(false);
}
